import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCurrency } from '../../../../utils/numberFormatter';
import {
  faFileSignature,
  faDollarSign,
  faCalendar,
  faCreditCard,
  faCircleInfo,
  faExclamationTriangle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { PAYMENT_METHODS, PRIMARY_PAYMENT_METHODS_COUNT } from '../../../../constants/acceptedPaymentMethods';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import QRCode from 'qrcode';

const MySwal = withReactContent(Swal);

const defaultDueDayLabel = '1°';
const dueDayEnv = typeof process.env.REACT_APP_CONFIRMATION_DUE_DAY === 'string' && process.env.REACT_APP_CONFIRMATION_DUE_DAY.trim() !== ''
  ? process.env.REACT_APP_CONFIRMATION_DUE_DAY.trim()
  : defaultDueDayLabel;

const Variant3ConfirmationView = ({
  selectedOffer,
  isProcessing,
  onConfirmButtonClick,
  clientData
}) => {
  const dueDayLabel = selectedOffer?.dueDayLabel ?? dueDayEnv;
  const [showAllPaymentMethods, setShowAllPaymentMethods] = useState(false);
  const [failedLogos, setFailedLogos] = useState({});

  const visiblePaymentMethods = showAllPaymentMethods
    ? PAYMENT_METHODS
    : PAYMENT_METHODS.slice(0, PRIMARY_PAYMENT_METHODS_COUNT);

  const handlePaymentLogoError = (methodId) => {
    setFailedLogos((prev) => {
      if (prev[methodId]) return prev;
      return { ...prev, [methodId]: true };
    });
  };

  const handleShareMiCuenta = async () => {
    if (!clientData?.my_account_url) return;

    try {
      const canvas = document.createElement('canvas');
      await QRCode.toCanvas(canvas, clientData.my_account_url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });

      const qrDataUrl = canvas.toDataURL();

      MySwal.fire({
        title: 'Portal Mi Cuenta',
        html: `
          <div class="text-center">
            <img src="${qrDataUrl}" class="mb-4" alt="QR Code" />
            <p class="mb-3">El cliente puede escanear este código QR para acceder a "Mi Cuenta", para realizar gestiones y consultas.</p>
            
            <div class="alert alert-warning d-flex align-items-center gap-2 mb-4 p-2" style="font-size: 0.9rem; max-width: 100%;">
              <div class="container-fluid">
                <div class="d-flex align-items-center gap-2">
                  <i class="bi bi-exclamation-triangle-fill"></i>
                  <div class="text-start">
                    Este link es único y personal para <strong>${clientData.firstName} ${clientData.lastName}</strong>. 
                    No debe ser compartido con otros clientes.
                  </div>
                </div>
              </div>
            </div>

            <hr />
            <div class="row g-3">
              <div class="col-6">
                <p class="text-muted small mb-2">Copia este enlace:</p>
                <div class="input-group">
                  <input 
                    type="text" 
                    value="${clientData.my_account_url}" 
                    class="form-control form-control-sm"
                    readonly
                  />
                  <button 
                    class="btn btn-outline-secondary copy-button"
                    type="button"
                    style="border: 1px solid #dce7f1 !important"
                    onclick="copyToClipboard('${clientData.my_account_url}', this)"
                  >
                    <i class="bi bi-clipboard"></i>
                  </button>
                </div>
              </div>
              <div class="col-6">
                <p class="text-muted small mb-2">O comparte con el cliente:</p>
                <a 
                  href="https://wa.me/?text=${encodeURIComponent(
                    `Hola ${clientData.firstName}, aquí puedes ver toda la información de tus préstamos y realizar gestiones: ${clientData.my_account_url}`
                  )}" 
                  target="_blank" 
                  class="btn btn-success w-100"
                  style="background-color: #25D366; border-color: #25D366"
                >
                  <i class="bi bi-whatsapp me-2"></i>
                  Enviar por WhatsApp
                </a>
              </div>
            </div>
          </div>
        `,
        showConfirmButton: false,
        showCloseButton: true,
        didOpen: () => {
          window.copyToClipboard = (text, button) => {
            navigator.clipboard.writeText(text).then(() => {
              const originalContent = button.innerHTML;
              button.innerHTML = '<i class="bi bi-check"></i>';
              button.disabled = true;
              setTimeout(() => {
                button.innerHTML = originalContent;
                button.disabled = false;
              }, 2000);
            });
          };
        }
      });
    } catch (error) {
      console.error('Error generando QR:', error);
    }
  };

  return (
    <div className="container rg-sale-confirmation-step rg-sale-confirmation-step--variant3">
      <h2 className="text-center rg-sale-step__title">
        <FontAwesomeIcon icon={faFileSignature} className="me-2" />
        Resumen antes de confirmar
      </h2>

      <div className="rg-summary-hero summary-section">
        <div className="rg-kpi-grid">
          <div className="rg-kpi">
            <span className="rg-kpi__label">
              <FontAwesomeIcon icon={faDollarSign} className="me-1" />
              Monto
            </span>
            <strong className="rg-kpi__value rg-kpi__value--primary rg-kpi__value--money">
              {formatCurrency(selectedOffer.originalAmount || selectedOffer.amount, 2)}
            </strong>
          </div>

          <div className="rg-kpi">
            <span className="rg-kpi__label">
              <FontAwesomeIcon icon={faCalendar} className="me-1" />
              Cuotas
            </span>
            <strong className="rg-kpi__value">{selectedOffer.term} cuotas</strong>
          </div>

          <div className="rg-kpi">
            <span className="rg-kpi__label">
              <FontAwesomeIcon icon={faDollarSign} className="me-1" />
              Cuota
            </span>
            <strong className="rg-kpi__value rg-kpi__value--money">
              {formatCurrency(selectedOffer.monthlyPayment, 2)}
            </strong>
          </div>

          <div className="rg-kpi">
            <span className="rg-kpi__label">
              <FontAwesomeIcon icon={faCalendar} className="me-1" />
              Primer vencimiento
            </span>
            <strong className="rg-kpi__value">{selectedOffer.displayDate || '-'}</strong>
          </div>
        </div>
      </div>

      <div className="rg-payments-card terms-section">
        <div className="d-flex align-items-center gap-2 mb-2">
          <FontAwesomeIcon icon={faCreditCard} className="rg-payments-card__icon" />
          <h6 className="mb-0 rg-sale-section__title">Medios de pago disponibles</h6>
        </div>

        <ul className="rg-payment-chips list-unstyled mb-0" role="list">
          {visiblePaymentMethods.map((method) => {
            const shouldUseLogo = Boolean(method.logoSrc && !failedLogos[method.id]);

            return (
              <li
                key={method.id}
                className={`rg-payment-chip ${shouldUseLogo ? 'rg-payment-chip--logo' : ''}`}
                title={method.label}
                aria-label={method.label}
              >
                {shouldUseLogo ? (
                  <img
                    src={method.logoSrc}
                    alt={method.label}
                    className="rg-payment-chip__logo"
                    loading="lazy"
                    onError={() => handlePaymentLogoError(method.id)}
                  />
                ) : (
                  <>
                    <FontAwesomeIcon icon={method.icon} className="rg-payment-chip__icon" aria-hidden />
                    <span>{method.label}</span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        {!showAllPaymentMethods && PAYMENT_METHODS.length > PRIMARY_PAYMENT_METHODS_COUNT && (
          <div className="rg-payment-methods-more">
            <button
              type="button"
              className="rg-btn rg-btn--link rg-payment-methods-more__btn"
              onClick={() => setShowAllPaymentMethods(true)}
            >
              Ver todos los métodos
            </button>
          </div>
        )}
      </div>

      <div className="rg-callout-wrap">
        <div className="rg-callout">
          <FontAwesomeIcon icon={faExclamationTriangle} className="rg-callout__icon" />
          <p className="mb-0">
            Vencimiento habitual: <strong>{dueDayLabel}</strong> de cada mes. En mora, se aplican intereses
            punitorios segun condiciones vigentes.
          </p>
        </div>

        <div className="rg-callout rg-callout--info">
          <FontAwesomeIcon icon={faCircleInfo} className="rg-callout__icon" />
          <p className="mb-0">
            Recordatorio: informa al cliente el acceso a la plataforma de autogestion para ver su
            resumen y elegir como pagar.
          </p>
        </div>
      </div>

      <div className="text-center rg-confirmation-cta-wrap">
        <button
          className="rg-btn rg-btn--primary rg-btn--lg rg-sale-confirmation-step__cta"
          onClick={onConfirmButtonClick}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Procesando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              {selectedOffer?.isDownpaymentPlan ? 'Realizar Pago Inicial y Confirmar' : 'Confirmar y Generar Prestamo'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Variant3ConfirmationView;
